import { Injectable, inject } from '@angular/core';
import {DatasetService} from '../../services/api/fit-api/dataset.service';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {SearchItem} from '../../models/search-item';
import FinancialHealthService from '../../services/financial-health.service';
import {map} from 'rxjs/operators';
import {OutlookIndicator} from '../../models/outlook-indicator';
import {FitApiService} from '../../services/api/fit-api/fit-api.service';
import {LocalGovernment} from '../../services/api/fit-api/models/local-government';
import {GovernmentType} from '../../services/api/fit-api/models/government-type';
import {SnapshotWithDetail} from '../../services/api/fit-api/models/datasets/snapshot';
import {AccountDescriptor} from '../../services/api/fit-api/models/snapshots/account-descriptor';
import {FinancialSummarySection} from '../../services/api/fit-api/models/snapshots/financial-summary-section';
import {FinancialSummaryService} from '../../services/financial-summary.service';
import {lowerCaseFirst} from '../../services/functions/lower-case-first';
import {hasKey} from '../../services/functions/has-key';
import {CurrencySuffixPipe} from '../../services/pipes/currency-suffix.pipe';
import {removeDuplicates} from '../../services/functions/array/remove-duplicates';
import {SchoolsWithDetail} from '../../services/api/fit-api/models/datasets/schools';
import {UpperCasePipe} from '@angular/common';
import {OUTLOOKS} from '../../models/outlook.config';

@Injectable({
  providedIn: 'root'
})
export class OmniSearchService {
	private fitApiService = inject(FitApiService);
	private upperCasePipe = inject(UpperCasePipe);
	private currencySuffixPipe = inject(CurrencySuffixPipe);
	private datasetService = inject(DatasetService);
	private financialSummaryService = inject(FinancialSummaryService);


	searchItems?: Observable<Array<SearchItem>>;

	// Get latest datasets
	datasetsWithDetail = this.datasetService.getDefaultFinancialsDatasets().pipe(
		// create an observable call for each dataset and recombine with forkJoin
		switchMap(datasets =>
			forkJoin(
				datasets.map(dataset => this.datasetService.getDatasetDetail(dataset.datasetType, dataset.id))
			)
		)
	);

	// Get default fiscal year shared between the datasets
	defaultFiscalYear: Observable<number> = this.datasetService.getDefaultYear(this.datasetsWithDetail);

	// Get list of fsSection dollar categories to use for search items
	fsSections = this.datasetsWithDetail.pipe(map(datasets => {
		const allSections = datasets.map(dataset =>
			dataset.detail.financialSummarySections ?? []
		).flat().filter(fs => fs.id !== 80);  // Filter out balance sheet sections

		return removeDuplicates(allSections, 'id').map(fs =>
			new SearchItem(
				'Dollars Category',
				fs,
				fs.logicalAccount,
				fs.name,
				'routerLink',
				[`/explore/dollars/bars`, fs.logicalAccount],
				// Populate fields & additional search text in forkJoin
			));
		})
	)

	// Get list of BARS dollar categories to use for search items
	accounts = this.datasetsWithDetail.pipe(map(datasets => {
			const allAccounts = datasets.map(dataset =>
				dataset.detail.accountDescriptors ?? []
			).flat().filter(bars =>
				!bars.deletedFromHierarchy && bars.primeAccountId !== bars.id && bars.fsSectionId !== 80
			);

			return removeDuplicates(allAccounts, 'id').map(bars =>
				new SearchItem(
					'Dollars Category',
					bars,
					bars.logicalAccount,
					bars.name,
					'routerLink',
					[`/explore/dollars/bars`, bars.logicalAccount],
					// Populate fields & additional search text in forkJoin
				));
		})
	)

	// Get Schedule1 Aggregations to display total dollars for Dollar Category search items;
	// uses the "ByGovType" version since this returns less data than the "ByGovt" version
	sch1Aggregations = forkJoin([this.datasetsWithDetail, this.defaultFiscalYear])
		.pipe(switchMap(([datasets, fiscalYear]) => {
			// Prepare Schedule 1 Aggregation query filters - we want all FSSection & BARS Account level detail
			const queryOverrides = new Map(FinancialSummaryService.barsHierarchy.map(level => [level, `ne null`]));
			// Fetch Schedule 1 data
			return forkJoin(datasets.map(dataset => {
				if (dataset instanceof SnapshotWithDetail) {
					return this.financialSummaryService.getSchedule1AggregationsByGovType(dataset, fiscalYear, fiscalYear, queryOverrides);
				} else if (dataset instanceof SchoolsWithDetail) {
					return this.financialSummaryService.getOSPIFinancialReportAggregationsByGovType(dataset, fiscalYear, fiscalYear, queryOverrides);
				}
				return of([]);
			})).pipe(map(x => x.flat()));
		}));

	// Get outlook summaries to display outlooks for Government & Government Type search types
	outlooksByGovernment = forkJoin([this.datasetsWithDetail, this.defaultFiscalYear])
		.pipe(switchMap(([datasets, fiscalYear]) => {
			return forkJoin(datasets.map(dataset =>
				this.fitApiService.getIndicatorSummaries(dataset, fiscalYear)
			)).pipe(
				map(summaries => summaries.flat())
			);
		}));

	// Build outlook search items
	outlooks = of(OUTLOOKS.map(o =>
		new SearchItem(
			'Financial Health',
			o,
			o.outlook,
			OutlookIndicator.aggregate([new OutlookIndicator(o.outlook)]) ?? new OutlookIndicator(o.outlook),
			'routerLink',
			[`/explore/financial-health`, { filterOutlook: o.outlook }],
			// Populate fields & additional search text in forkJoin
		)
	));

	// Build government search items
	governments = this.fitApiService.governmentsForSearch.pipe(map(govts => govts.map(govt =>
		new SearchItem(
			'Government',
			govt,
			govt.mcag,
			govt.fullNameForSearching ?? '',
			'routerLink',
			[`/explore/government`, govt.mcag]
			// Populate fields & additional search text in forkJoin
		))
	));

	// Build government type search items
	governmentTypes = this.fitApiService.governmentTypesForList.pipe(map(types => types.map(type =>
		new SearchItem(
			'Government Type',
			type,
			type.code,
			type.descriptionPlural,
			'routerLink',
			[`/explore/government-type`, type.code],
			// Populate fields & additional search text in forkJoin
		))
	));

	constructor() {
		// Concatenate all search items together and annotate with additional metrics or properties (FHI, etc.)
		this.searchItems = forkJoin([
			this.defaultFiscalYear,
			this.fitApiService.countiesForList,
			this.outlooks,
			this.governments,
			this.governmentTypes,
			this.fsSections,
			this.accounts,
			this.outlooksByGovernment,
			this.sch1Aggregations
		]).pipe(map(([fiscalYear, counties, outlooks, govts, types, fsSections, accounts, outlooksByGovt, sch1Agg]) => {
			// Populate additional data fields for local governments
			govts.forEach(item => {
				if (item.detailFields.length === 0) {
					const govt = item.object as LocalGovernment;
					// Add county field
					const county = counties.find(c => govt.countyCodes?.includes(c.countyCode));
					if (govt && county && county.countyCode !== 50 && govt.govTypeCode !== '06') {  // Don't show "county" field for Statewide "county code" & County gov types
						item.detailFields.push( { label: 'County', value: this.upperCasePipe.transform(county?.countyName ?? ''), alwaysDisplay: false } );
					}
					// Add govt type field
					const type = types.find(t => t.id === govt.govTypeCode);
					if (govt && type) {
						item.detailFields.push( { label: 'Type', value: type.object as GovernmentType, type: 'Government Type', alwaysDisplay: true } );
					}
					// Get FHI Outlook
					const outlookSummary = outlooksByGovt.find(o => o.mcag === govt.mcag);
					if (outlookSummary) {
						const outlookIndicator = OutlookIndicator.aggregate(outlookSummary.indicatorOutlooks);
						item.detailFields.push( { label: 'Outlook', value:  outlookIndicator, type: 'Outlook Indicator', alwaysDisplay: false } );
						item.setAdditionalStringForSearch(outlookIndicator.outlook ?? '');
					}
				}
			})

			// Populate additional data fields & search strings for government types
			types.forEach(item => {
				if (item.detailFields.length === 0) {
					const type = item.object as GovernmentType;
					if (type) {
						item.detailFields.push({ label: 'Governments', value: type.activeCount, alwaysDisplay: true });
						item.setAdditionalStringForSearch(type.description);

						// Get FHI Outlooks for this gov Type
						const outlookSummaries = outlooksByGovt.filter(govt => govt.govTypeCode === type.code).map(govt =>
							OutlookIndicator.aggregate(govt.indicatorOutlooks)
						);
						item.detailFields.push({ label: '', value: outlookSummaries, type: 'Outlook Indicators', alwaysDisplay: false });
					}
				}
			});

			// Populate additional data fields & search text for financial health outlooks
			outlooks.forEach(item => {
				if (item.detailFields.length === 0) {
					const outlookIndicator = item.object as OutlookIndicator;

					// Get # of governments that match this outlook search item (Good, Cautionary, Concerning, etc.)
					const outlookSummaries = outlooksByGovt.map(govt =>
						OutlookIndicator.aggregate(govt.indicatorOutlooks)
					).filter(govt => govt.outlook === outlookIndicator.outlook);
					item.detailFields.push({ label: 'Governments', value: outlookSummaries.length, alwaysDisplay: true });

					item.setAdditionalStringForSearch('Financial Health Indicator FHI Outlook');
				}
			});

			// Populate FSSection dollars category with additional data fields & search text
			fsSections.forEach(item => {
				if (item.detailFields.length === 0) {
					const fsSection = item.object as FinancialSummarySection;
					if (fiscalYear) {
						const total = sch1Agg.filter(s1 => s1.fsSectionId === fsSection.id)
							.reduce((sum, s1) => { return sum + s1.totalAmountExclIntlSrvc }, 0);
						item.detailFields.push( { label: `FY ${fiscalYear}`, value: this.currencySuffixPipe.transform(total, 1), alwaysDisplay: true } );
					}
				}
			});

			// Populate BARS segments' dollars category with additional data fields & search text
			accounts.forEach(item => {
				if (item.detailFields.length === 0) {
					const account = item.object as AccountDescriptor;

					// Lookup FS Section name for BARS Account
					const fsSection = fsSections.map(i => i.object as FinancialSummarySection).find(fs => fs.id === account.fsSectionId);
					if (fsSection) {
						item.detailFields.push( { label: 'Category', value: fsSection.name, alwaysDisplay: true });
					}

					// Add total dollars for the current display year
					if (fiscalYear && account.acctSgmt) {
						const fieldName = lowerCaseFirst(account.acctSgmt + 'Id');
						const total = sch1Agg.filter(s1 => hasKey(s1, fieldName) && s1[fieldName] === account.id)
							.reduce((sum, s1) => { return sum + s1.totalAmountExclIntlSrvc }, 0);
						item.detailFields.push( { label: `FY ${fiscalYear}`, value: this.currencySuffixPipe.transform(total, 1), alwaysDisplay: true } );
					}
				}
			});

			return outlooks.concat(govts).concat(types).concat(fsSections).concat(accounts);
		}));
	}

}
